/*
 * 内部実装の変更を伝播させず済むよう、型定義だけ公開
 * 利用側にはfactory関数か事前定義の定数からのみオブジェクトを参照させる
 */
import { fromNullable, Option } from 'fp-ts/lib/Option'

export type ConditionGroup = InnerConditionGroup

export type Condition = InnerCondition

class InnerConditionGroup {
  constructor(
    readonly label: string,
    readonly conditions: readonly Condition[],
    private readonly queryTranslator: QueryTranslator,
    private readonly multiSelect: boolean
  ) {
  }

  change({ target, applied }: { target: Condition, applied: boolean }): ConditionGroup {
    let conditions = this.conditions

    // 単一選択項目の変更時はいったん全選択肢を解除する
    if (!this.multiSelect) {
      conditions = conditions.map(cond => cond.asNotApplied())
    }

    return createConditionGroup(
      this.label,
      conditions.map(cond => cond.equals(target) ? cond.toApplied(applied) : cond),
      this.queryTranslator,
      this.multiSelect
    )
  }

  reset(): ConditionGroup {
    return createConditionGroup(
      this.label,
      this.conditions.map(cond => cond.asNotApplied()),
      this.queryTranslator,
      this.multiSelect
    )
  }

  translateToQuery(): TranslatedQuery {
    const applied = this.conditions.filter(cond => cond.applied)

    return this.queryTranslator.translate(applied)
  }

  /**
   * クエリ変換後のkey-valueを基準にConditionを検索する
   */
  findConditionAsQuery(queryKey: string, queryValue: string): Option<Condition> {
    const found = this.conditions.find((cond) => {
      const translated = this.queryTranslator.translate([cond])

      return translated.getValue(queryKey) === queryValue
    })

    return fromNullable(found)
  }
}
export function createConditionGroup(
  label: string,
  conditions: readonly Condition[],
  queryTranslator: QueryTranslator,
  multiSelect?: boolean
): ConditionGroup {
  return new InnerConditionGroup(label, conditions, queryTranslator, !!multiSelect)
}
export const emptyConditionGroup: ConditionGroup = createConditionGroup(
  '',
  [] as Condition[],
  {
    translate: () => TranslatedQuery.empty,
  }
)

class InnerCondition {
  constructor(
    readonly id: string,
    readonly label: string,
    readonly value: string,
    readonly applied: boolean
  ) {
  }

  asApplied(): Condition {
    return new InnerCondition(this.id, this.label, this.value, true)
  }
  asNotApplied(): Condition {
    return new InnerCondition(this.id, this.label, this.value, false)
  }
  toApplied(applied: boolean): Condition {
    return applied ? this.asApplied() : this.asNotApplied()
  }

  equals(other: Condition): boolean {
    return this.id === other.id
  }
}
export function createCondition(
  id: string,
  label: string,
  value: string
): Condition {
  return new InnerCondition(id, label, value, false)
}

/**
 * 条件オブジェクトをクエリ文字列に変換する
 *
 * どのような文字列へ変換するかはmodel内部では決定せず、
 * 利用側がどのような変換オブジェクトを注入するかに委ねる
 */
export interface QueryTranslator {
  translate(conditions: Condition[]): TranslatedQuery
}

export class TranslatedQuery {
  static get empty() {
    return new TranslatedQuery({})
  }

  constructor(private readonly query: Readonly<{ [key: string]: string | unknown }>) {
  }

  concat(other: TranslatedQuery): TranslatedQuery {
    return new TranslatedQuery({ ...this.query, ...other.query })
  }
  /**
   * 各クエリのキーと値のペアの配列を返す
   */
  keyValues(): ReadonlyArray<Readonly<{ key: string, value: string | unknown }>> {
    return Object.entries(this.query)
      .map(([key, value]) => ({ key, value }))
      .filter(v => !!v.value)
  }

  getValue(queryKey: string): string | unknown | null {
    return this.query[queryKey] || null
  }
}
